import {
    ACCOUNT_ROLES,
    ALL_COMMODITIES,
    DEFAULT_GROUP,
    OTHER_ACCOUNT_LABEL,
    PLATFORM_DEFAULTS,
    PLATFORM_LABEL,
    TARIFF_CATEGORIES,
    TARIFF_CATEGORY_GROUPS,
    TARIFF_COMMODITIES,
    UNTAGGED,
    UNTAGGED_LABEL,
} from './constants';

const CATEGORY_LABELS = Object.fromEntries(TARIFF_CATEGORIES.map(({ value, label }) => [value, label]));
const COMMODITY_ORDER = TARIFF_COMMODITIES.map(({ value }) => value);

// A category this build doesn't know (added to the enum before the UI) is shown as Untagged
// rather than dropped from the page.
export const categoryKeyOf = (tariff) => (CATEGORY_LABELS[tariff?.category] ? tariff.category : UNTAGGED);

export const categoryLabel = (key) => CATEGORY_LABELS[key] || UNTAGGED_LABEL;

export const commodityLabel = (value) => TARIFF_COMMODITIES.find((c) => c.value === value)?.label || value;

const groupCategories = (group) =>
    (
        TARIFF_CATEGORY_GROUPS.find((g) => g.value === group) ||
        TARIFF_CATEGORY_GROUPS.find((g) => g.value === DEFAULT_GROUP)
    ).categories;

export const tariffsInGroup = (tariffs, group) => {
    const categories = new Set(groupCategories(group));
    return (tariffs || []).filter((tariff) => categories.has(categoryKeyOf(tariff)));
};

// Every section of the group, in display order, including empty ones (they render "None yet.").
export const sectionsForGroup = (tariffs, group) =>
    groupCategories(group).map((key) => ({
        key,
        label: `${categoryLabel(key)} Tariffs`,
        tariffs: (tariffs || []).filter((tariff) => categoryKeyOf(tariff) === key),
    }));

// No chips selected matches everything. Otherwise any selected chip can match: a tariff tagged
// All Commodities applies to every commodity, so it matches each specific chip, while the All
// Commodities chip itself matches only tariffs tagged All Commodities.
export const matchesCommodities = (tags, selected) => {
    if (!selected?.length) return true;
    const tagSet = new Set(tags || []);
    return selected.some((value) =>
        value === ALL_COMMODITIES ? tagSet.has(ALL_COMMODITIES) : tagSet.has(value) || tagSet.has(ALL_COMMODITIES)
    );
};

// Each chip's count is the number of tariffs that selecting only that chip would show.
export const countByCommodity = (tariffs) =>
    Object.fromEntries(
        COMMODITY_ORDER.map((value) => [
            value,
            (tariffs || []).filter((tariff) => matchesCommodities(tariff.commodity_tags, [value])).length,
        ])
    );

// All Commodities and the specific commodities exclude each other: choosing one side clears the
// other. Known tags come back in display order; tags this build doesn't know are kept at the end
// so editing a tariff never silently drops them.
export const toggleCommodityTag = (tags, value) => {
    const next = new Set(tags || []);
    if (next.has(value)) {
        next.delete(value);
    } else if (value === ALL_COMMODITIES) {
        next.clear();
        next.add(ALL_COMMODITIES);
    } else {
        next.delete(ALL_COMMODITIES);
        next.add(value);
    }
    return [
        ...COMMODITY_ORDER.filter((known) => next.has(known)),
        ...[...next].filter((tag) => !COMMODITY_ORDER.includes(tag)),
    ];
};

// Hasura casts enum-array input reliably only from the Postgres literal form ('{A,B}'); raw JSON
// arrays sometimes fail (same workaround as createCorpClient in onward-functions). Enum values
// never contain commas, quotes or spaces, so nothing needs escaping. Empty is '{}', not null,
// because commodity_tags is NOT NULL.
export const toPgArrayLiteral = (values) => `{${(values || []).join(',')}}`;

// Sort ranks: each matched role contributes its ACCOUNT_ROLES index, so "Shipper" ('0') sorts
// before "Shipper / Carrier" ('01'), then "Carrier" ('1'). Other accounts and platform defaults go last.
const OTHER_ACCOUNT_RANK = '8';
const PLATFORM_RANK = '9';

const roleSummary = (roles) => {
    const matched = ACCOUNT_ROLES.map((role, idx) => ({ ...role, idx })).filter((role) =>
        role.keys.some((key) => roles?.[key])
    );
    if (!matched.length) return { label: OTHER_ACCOUNT_LABEL, rank: OTHER_ACCOUNT_RANK };
    return {
        label: matched.map((role) => role.label).join(' / '),
        rank: matched.map((role) => role.idx).join(''),
    };
};

// "Shipper", "Carrier", "Contractor", or several joined ("Shipper / Carrier"), as CorporateTags
// labels accounts; "Account" when none of those roles is present.
export const accountRoleLabel = (roles) => roleSummary(roles).label;

// One pill per account the tariff is assigned to (the override's client), labeled with that
// account's role. Platform-wide defaults have no account, so they get their own pills; any other
// row without an account is a retired one and is skipped.
export const assignmentTags = (overrides) => {
    const tags = {};
    (overrides || []).forEach((override) => {
        if (!override.client_id) {
            const name = PLATFORM_DEFAULTS[override.algo_type];
            if (name) {
                tags[override.algo_type] = { key: override.algo_type, role: PLATFORM_LABEL, name, rank: PLATFORM_RANK };
            }
            return;
        }
        if (tags[override.client_id]) return;
        const { label, rank } = roleSummary(override.client?.user?.roles);
        tags[override.client_id] = {
            key: override.client_id,
            role: label,
            name: override.client?.business_name || 'Unnamed account',
            rank,
        };
    });
    return Object.values(tags)
        .sort((a, b) => (a.rank === b.rank ? a.name.localeCompare(b.name) : a.rank < b.rank ? -1 : 1))
        .map(({ rank, ...tag }) => tag);
};
